import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { getApiErrorMessage } from '@/shared/lib/getApiErrorMessage';
import { isUnreachedRequestError } from '@/shared/api';
import type {
  IdentifyByNameResponse,
  LoginCandidate,
  LoginTarget,
  SendOtpResponse,
} from '@aegislab/shared';
import { useStepTransition } from '../../model/useStepTransition';
import { getBackStep } from '../../lib/loginStepUtils';
import {
  loadLastLoginAccount,
  loadLastLoginMethod,
  orderLoginMethods,
  pickAutoLoginMethod,
  saveLastLoginAccount,
  saveLastLoginMethod,
  type LoginMethod,
} from '../../lib/lastLoginMethod';
import type { Transition } from '../../model/useStepTransition';
import { usePasskeyLogin } from '../../model/usePasskeys';
import { stepContent, stepExiting, stepEntering } from '../AuthSceneLayout';
import { LoginStepContent } from './LoginStepContent';
import type { LoginStep } from './loginStepView.types';
import * as s from './LoginStepForm.css';

export type { LoginStep };

// 서버 한도는 `POST /auth/login/send-otp` 60초 3회(auth.controller.ts). 그보다 보수적으로
// 한 번 보내면 60초를 쉬어, 창 경계를 몰라도 한도에 닿지 않는다.
const OTP_RESEND_COOLDOWN_SECONDS = 60;

// 스텝 진입 시 aria-live 안내 문구 — 확정 계약: docs/archive/auth-dead-wiring/decisions-2026-07-31.md
const STEP_ANNOUNCEMENT: Record<LoginStep, string> = {
  name: '이름 입력 단계',
  method: '로그인 방법 선택 단계',
  otp: '인증코드 입력 단계',
  password: '비밀번호 입력 단계',
  success: '로그인 완료',
};

export interface UseLoginStepFormOptions {
  loading?: boolean;
  error?: string;
  onAuthenticate?: (target: LoginTarget, password: string) => void | Promise<void>;
  onIdentifyByName?: (username: string) => Promise<IdentifyByNameResponse>;
  onIdentifyByEmail?: (username: string, email: string) => Promise<IdentifyByNameResponse>;
  onSendOtp?: (ticket: string) => Promise<SendOtpResponse>;
  onVerifyOtp?: (ticket: string, code: string) => void | Promise<void>;
  onPasskeyLogin?: (target: LoginTarget) => Promise<void>;
  onForgotPassword?: () => void;
  /** 미가입 시 외부 회원가입 라우트로 이동 */
  onSignupNavigate?: () => void;
  initialStep?: LoginStep;
  onAuthComplete?: () => void;
  /** 스토리·시안용: IDENT 를 이미 통과한 상태로 시작한다 */
  initialIdentity?: { realName: string } & LoginCandidate;
}

export interface LoginStepFormSceneProps {
  transition: Transition;
  frameGlitch: boolean;
  stepAnnouncement: string;
  sceneExiting: boolean;
  currentStep: LoginStep;
  /** IDENT 를 통과한 뒤의 이름. 전엔 undefined */
  playerName?: string;
  /** 확인된 계정의 패스키 보유 여부. IDENT 전엔 undefined */
  hasPasskey?: boolean;
}

export interface UseLoginStepFormReturn {
  /** Props to spread into AuthSceneLayout */
  sceneProps: LoginStepFormSceneProps;
  /** Rendered form content (goes inside AuthSceneLayout children) */
  formContent: ReactNode;
}

/**
 * Hook that encapsulates multi-step login form logic and UI.
 *
 * Returns `sceneProps` for AuthSceneLayout and `formContent` to render inside it.
 * Steps: name → method → otp | password → success
 */
export function useLoginStepForm({
  loading = false,
  error,
  onAuthenticate,
  onIdentifyByName,
  onIdentifyByEmail: _onIdentifyByEmail,
  onSendOtp,
  onVerifyOtp,
  onPasskeyLogin,
  onForgotPassword,
  onSignupNavigate,
  initialStep = 'name',
  onAuthComplete,
  initialIdentity,
}: UseLoginStepFormOptions): UseLoginStepFormReturn {
  // Passkey login
  const passkeyLogin = usePasskeyLogin();
  const supportsPasskey = browserSupportsWebAuthn();

  // Name step state
  const [realName, setRealName] = useState(initialIdentity?.realName ?? '');
  const [nameError, setNameError] = useState('');
  const [unregistered, setUnregistered] = useState(false);
  const [identifyResult, setIdentifyResult] = useState<IdentifyByNameResponse | null>(() =>
    initialIdentity ? { exists: true, ...initialIdentity } : null
  );
  const [candidates, setCandidates] = useState<LoginCandidate[]>([]);
  const savedCandidatesRef = useRef<LoginCandidate[]>([]);
  // 식별이 정한 계정 — 서버는 userId·전체 이메일 대신 로그인 표와 가린 이메일만 준다(퀘스트 fa6787e3)
  const [account, setAccount] = useState<LoginCandidate | null>(initialIdentity ?? null);

  // OTP step state
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);

  // Password step state
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // 마지막으로 사용한 로그인 방법 — 마운트 시점 값을 고정해 이번 세션 저장에 흔들리지 않게 한다
  const [lastMethod] = useState(() => loadLastLoginMethod());
  const [lastAccount] = useState(() => loadLastLoginAccount());
  // 자동으로 연 패스키가 끝나지 않았을 때 방법 선택 화면에 남기는 한 줄
  const [methodNotice, setMethodNotice] = useState('');

  // Shared state
  const [submitting, setSubmitting] = useState(false);

  // Refs for focus management
  const nameInputRef = useRef<HTMLInputElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // 실패한 시도에 쿨다운을 걸지 말지는 "그 시도가 서버 한도를 깎았는가"로 갈린다.
  // 한도를 세는 throttler 가드는 요청이 도착해야 도므로, 도착하지 못한 요청은 한 칸도
  // 깎지 않는다 — 그런 실패까지 붙잡으면 사용자를 아무 이유 없이 기다리게 한다.
  const applyOtpCooldownOnFailure = useCallback((err: unknown) => {
    if (isUnreachedRequestError(err)) return;
    setOtpResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
  }, []);

  // OTP resend cooldown timer
  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const timer = setTimeout(() => setOtpResendCooldown((p) => p - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpResendCooldown]);

  // Focus callback for step transition
  // 비밀번호 스텝의 이메일은 확정값이라 포커스가 갈 자리가 아니다 — 언제나 비밀번호로 간다.
  const onStepEntered = useCallback((nextStep: LoginStep) => {
    if (nextStep === 'name') nameInputRef.current?.focus();
    else if (nextStep === 'otp') otpInputRef.current?.focus();
    else if (nextStep === 'password') passwordInputRef.current?.focus();
  }, []);

  // Step transition
  const {
    step,
    transition,
    frameGlitch,
    isTransitioning,
    stepAnnouncement,
    triggerStepTransition,
    sceneExiting,
  } = useStepTransition<LoginStep>({
    initialStep,
    successStep: 'success',
    onComplete: onAuthComplete,
    onStepEntered,
    announcements: STEP_ANNOUNCEMENT,
  });

  // --- Handlers ---

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = realName.trim();
    if (trimmed.length < 2) {
      setNameError('이름을 2자 이상 입력해주세요');
      return;
    }
    setNameError('');
    setUnregistered(false);
    try {
      setSubmitting(true);
      const result = onIdentifyByName ? await onIdentifyByName(trimmed) : { exists: false };
      if (!result.exists) {
        setNameError('등록되지 않은 이름입니다');
        setUnregistered(true);
        return;
      }
      if (result.duplicates && result.candidates) {
        setCandidates(result.candidates);
        savedCandidatesRef.current = result.candidates;
        setIdentifyResult(result);
        return;
      }
      setIdentifyResult(result);
      const found: LoginCandidate = {
        ticket: result.ticket ?? '',
        accountKey: result.accountKey ?? '',
        maskedEmail: result.maskedEmail ?? '',
        hasPasskey: result.hasPasskey ?? false,
      };
      setAccount(found);
      await proceedWithAccount(found);
    } catch (err) {
      setNameError(getApiErrorMessage(err, '이름 확인 중 오류가 발생했습니다. 다시 시도해주세요.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCandidateSelect = (candidate: LoginCandidate) => {
    setAccount(candidate);
    setIdentifyResult((prev) => (prev ? { ...prev, ...candidate } : prev));
    setCandidates([]);
    void proceedWithAccount(candidate);
  };

  // 기억된 방법이 있으면 방법 선택 화면을 건너뛴다. 동명이인은 후보를 고르는 순간이 「다음」 자리다.
  // 실패하면 방법 선택 화면에 떨어뜨려 쿨다운과 다른 방법이 보이게 한다.
  const proceedWithAccount = async (picked: LoginCandidate) => {
    setMethodNotice('');
    const auto = pickAutoLoginMethod({
      lastMethod,
      hasPasskey: picked.hasPasskey,
      supportsPasskey,
    });
    if (auto === 'otp') {
      if (!(await handleSendOtp(picked))) triggerStepTransition('method');
      return;
    }
    triggerStepTransition('method');
    if (auto === 'passkey' && !(await handlePasskeyLogin(picked))) {
      setMethodNotice('패스키 로그인을 마치지 못했습니다. 다른 방법을 골라주세요.');
    }
  };

  // 기억은 로그인에 성공한 뒤에만 — 실패한 방법·계정이 다음 방문의 기본값이 되지 않게
  const rememberLogin = (method: LoginMethod, accountKey: string | undefined) => {
    saveLastLoginMethod(method);
    if (accountKey) saveLastLoginAccount(accountKey);
  };

  // picked 는 자동 진행 경로용 — 이름 확인과 같은 틱이라 account 상태가 아직 옛값이다
  const handleSendOtp = async (picked = account): Promise<boolean> => {
    if (!picked?.ticket) return false;
    try {
      setSubmitting(true);
      await onSendOtp?.(picked.ticket);
      setOtpCode('');
      setOtpError('');
      setOtpResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
      triggerStepTransition('otp');
      return true;
    } catch (err) {
      // 실패 문구는 상위(useAuth)가 error prop 으로 넘겨 errorBox 에 뜬다.
      // 여기서 setOtpError 를 해도 그 값은 otp 스텝에서만 렌더되는데, 발송이 실패하면
      // 그 스텝으로 넘어가지 못하므로 도달할 수 없다.
      applyOtpCooldownOnFailure(err);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(digits);
    if (digits.length === 6) {
      // auto-submit
      void handleOtpSubmitValue(digits);
    }
  };

  const handleOtpSubmitValue = async (code: string) => {
    if (!account?.ticket) return;
    setOtpError('');
    try {
      setSubmitting(true);
      await onVerifyOtp?.(account.ticket, code);
      rememberLogin('otp', account.accountKey);
      triggerStepTransition('success');
    } catch (err) {
      setOtpError(getApiErrorMessage(err, '인증코드가 올바르지 않습니다. 다시 확인해주세요.'));
      setOtpCode('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      setOtpError('6자리 인증코드를 입력해주세요');
      return;
    }
    await handleOtpSubmitValue(otpCode);
  };

  const handleResendOtp = async () => {
    if (!account?.ticket || otpResendCooldown > 0) return;
    try {
      setSubmitting(true);
      await onSendOtp?.(account.ticket);
      setOtpCode('');
      setOtpError('');
      setOtpResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setOtpError(getApiErrorMessage(err, '인증코드 재발송에 실패했습니다. 다시 시도해주세요.'));
      applyOtpCooldownOnFailure(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    const prev = getBackStep(step);
    if (!prev) return;
    if (prev === 'name' && savedCandidatesRef.current.length > 0) {
      setCandidates(savedCandidatesRef.current);
    }
    triggerStepTransition(prev);
  };

  const handleGoToPassword = () => triggerStepTransition('password');

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // 대상은 식별 단계가 정한 로그인 표다 — 사용자가 이메일을 다시 쓰지 않는다.
    if (!password) {
      setPasswordError('비밀번호를 입력해주세요');
      return;
    }
    setPasswordError('');
    try {
      setSubmitting(true);
      await onAuthenticate?.({ ticket: account?.ticket ?? '' }, password);
      rememberLogin('password', account?.accountKey);
      triggerStepTransition('success');
    } catch {
      // error is handled externally via error prop
    } finally {
      setSubmitting(false);
    }
  };

  // picked 는 자동 진행 경로용 — 이름 확인과 같은 틱이라 account 상태가 아직 옛값이다
  const handlePasskeyLogin = async (picked = account) => {
    const target = { ticket: picked?.ticket ?? '' };
    try {
      setSubmitting(true);
      if (onPasskeyLogin) {
        await onPasskeyLogin(target);
      } else {
        await passkeyLogin.mutateAsync(target);
      }
      rememberLogin('passkey', picked?.accountKey);
      triggerStepTransition('success');
      return true;
    } catch {
      // 글로벌 mutation onError 핸들러가 토스트 표시
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // --- Step className ---

  const getStepClassName = (t: Transition) => {
    if (t === 'exiting') return `${stepContent} ${stepExiting}`;
    if (t === 'entering') return `${stepContent} ${stepEntering}`;
    return stepContent;
  };

  // --- Step content ---

  const disabled = loading || submitting || isTransitioning;

  const methodOrder = orderLoginMethods({
    lastMethod,
    hasPasskey: identifyResult?.hasPasskey ?? false,
    supportsPasskey,
  });

  const methodHandlers: Record<LoginMethod, () => void> = {
    otp: () => {
      void handleSendOtp();
    },
    passkey: () => {
      void handlePasskeyLogin();
    },
    password: handleGoToPassword,
  };

  const stepContent_ = (
    <LoginStepContent
      step={step}
      handleNameSubmit={handleNameSubmit}
      nameInputRef={nameInputRef}
      realName={realName}
      setRealName={setRealName}
      setNameError={setNameError}
      setUnregistered={setUnregistered}
      setCandidates={setCandidates}
      nameError={nameError}
      disabled={disabled}
      candidates={candidates}
      lastAccount={lastAccount}
      handleCandidateSelect={handleCandidateSelect}
      isTransitioning={isTransitioning}
      savedCandidatesRef={savedCandidatesRef}
      setIdentifyResult={setIdentifyResult}
      submitting={submitting}
      unregistered={unregistered}
      onSignupNavigate={onSignupNavigate}
      account={account}
      methodNotice={methodNotice}
      methodOrder={methodOrder}
      passkeyPending={passkeyLogin.isPending}
      otpResendCooldown={otpResendCooldown}
      methodHandlers={methodHandlers}
      handleBack={handleBack}
      handleOtpSubmit={handleOtpSubmit}
      otpInputRef={otpInputRef}
      otpCode={otpCode}
      handleOtpChange={handleOtpChange}
      otpError={otpError}
      handleResendOtp={handleResendOtp}
      triggerStepTransition={triggerStepTransition}
      passwordInputRef={passwordInputRef}
      password={password}
      setPassword={setPassword}
      passwordError={passwordError}
      handlePasswordSubmit={handlePasswordSubmit}
      loading={loading}
      identifyResult={identifyResult}
      supportsPasskey={supportsPasskey}
      handlePasskeyLogin={() => handlePasskeyLogin()}
      error={error}
      onForgotPassword={onForgotPassword}
    />
  );

  // --- Return ---

  const sceneProps: LoginStepFormSceneProps = {
    transition,
    frameGlitch,
    stepAnnouncement,
    sceneExiting,
    currentStep: step,
    playerName: identifyResult ? realName.trim() || undefined : undefined,
    hasPasskey: identifyResult?.hasPasskey,
  };

  const formContent: ReactNode = (
    <>
      {error && (
        <div className={s.errorBox} role="alert">
          <p className={s.errorText}>{error}</p>
        </div>
      )}
      <div className={getStepClassName(transition)}>{stepContent_}</div>
    </>
  );

  return { sceneProps, formContent };
}
